import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { api } from '../../lib/api';
import type { PriceRange } from '../../types';
import {
  Spinner, EmptyState, Alert,
  inputClass, labelClass, primaryBtn,
} from '../../components/ui';

interface Editable {
  min_price: string;
  max_price: string;
  step: string;
}

export default function PriceRanges() {
  const [ranges, setRanges] = useState<PriceRange[]>([]);
  const [forms, setForms] = useState<Record<number, Editable>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api.get<PriceRange[]>('/price-ranges')
      .then((rows) => {
        setRanges(rows);
        const next: Record<number, Editable> = {};
        rows.forEach((r) => {
          next[r.id] = { min_price: String(r.min_price), max_price: String(r.max_price), step: String(r.step) };
        });
        setForms(next);
      })
      .catch(() => setError('Failed to load price ranges'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleSave = async (r: PriceRange) => {
    setSavingId(r.id);
    setError('');
    setMessage('');
    try {
      const form = forms[r.id];
      await api.put(`/price-ranges/${r.id}`, {
        min_price: Number(form.min_price),
        max_price: Number(form.max_price),
        step: Number(form.step),
      });
      setMessage(`Price range for ${r.currency} updated.`);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <Spinner label="Loading price ranges..." />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Price Ranges</h1>
        <p className="text-sm text-gray-500 mt-1">Configure the price buckets used by the property search price filter.</p>
      </div>

      {message && <Alert type="success">{message}</Alert>}
      {error && <Alert>{error}</Alert>}

      {ranges.length === 0 ? (
        <EmptyState message="No price ranges configured yet." />
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {ranges.map((r) => {
            const form = forms[r.id] || { min_price: '', max_price: '', step: '' };
            return (
              <div key={r.id} className="bg-white p-5 rounded-lg shadow-lg">
                <h2 className="font-semibold mb-4">{r.currency} Price Range</h2>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Minimum Price</label>
                    <input
                      type="number"
                      min={0}
                      className={inputClass}
                      value={form.min_price}
                      onChange={(e) => setForms({ ...forms, [r.id]: { ...form, min_price: e.target.value } })}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Maximum Price</label>
                    <input
                      type="number"
                      min={0}
                      className={inputClass}
                      value={form.max_price}
                      onChange={(e) => setForms({ ...forms, [r.id]: { ...form, max_price: e.target.value } })}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Step</label>
                    <input
                      type="number"
                      min={0}
                      className={inputClass}
                      value={form.step}
                      onChange={(e) => setForms({ ...forms, [r.id]: { ...form, step: e.target.value } })}
                    />
                  </div>
                  <button
                    onClick={() => handleSave(r)}
                    disabled={savingId === r.id}
                    className={primaryBtn}
                  >
                    <Save className="h-4 w-4" /> {savingId === r.id ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
